import * as Api from '@ViewModels';
import { SelectContent, SelectItem, SelectTrigger, SelectValue } from '@WebComponents/Select';
import React from 'react';
import { type ComboboxItem, EditModeCombobox, EditModeSelect } from '../context';
import { styleSheet } from '../styles';
import { SettingGroup } from './SettingGroup';
import { SettingRow } from './SettingRow';

interface IKeyFactSettingsGroupProps {
	keyFactSettings: Api.IKeyFactSettings;
	configuration: Api.IIntegrationConfiguration;
	provider: Api.IntegrationProvider;
	setKeyFactsSettings: (keyFactsSettings: Api.IKeyFactSettings) => void;
}

const birthdayDisplayOptions = [
	{ label: 'All', value: Api.BirthdayKeyFactDisplay.All },
	{ label: 'Clients Only', value: Api.BirthdayKeyFactDisplay.ClientOnly },
	{ label: 'Prospects Only', value: Api.BirthdayKeyFactDisplay.ProspectOnly },
];

export const KeyFactSettingsGroup: React.FC<IKeyFactSettingsGroupProps> = ({
	keyFactSettings,
	configuration,
	provider,
	setKeyFactsSettings,
}) => {
	const isPracticePantherSettings = provider === Api.IntegrationProvider.PracticePanther;
	const ppKeyFactSettings = isPracticePantherSettings ? (keyFactSettings as Api.IPracticePantherKeyFactSettings) : null;

	const isMicrosoftDynamics365SalesSettings = provider === Api.IntegrationProvider.MicrosoftDynamics365Sales;
	const microsoftDynamics365SalesKeyFactSettings = isMicrosoftDynamics365SalesSettings
		? (keyFactSettings as Api.IMicrosoftDynamics365SalesKeyFactSettings)
		: null;
	const microsoftDynamics365SalesConfiguration = isMicrosoftDynamics365SalesSettings
		? (configuration as Api.IMicrosoftDynamics365SalesIntegrationConfiguration)
		: null;

	const isSalesforceSettings = provider === Api.IntegrationProvider.Salesforce;
	const salesforceKeyFactSettings = isSalesforceSettings ? (keyFactSettings as Api.ISalesforceKeyFactSettings) : null;
	const salesforceConfiguration = isSalesforceSettings
		? (configuration as Api.ISalesforceIntegrationConfiguration)
		: null;

	const isServiceTitanSettings = provider === Api.IntegrationProvider.ServiceTitan;
	const serviceTitanKeyFactSettings = isServiceTitanSettings
		? (keyFactSettings as Api.IServiceTitanKeyFactSettings)
		: null;
	const serviceTitanConfiguration = isServiceTitanSettings
		? (configuration as Api.IServiceTitanIntegrationConfiguration)
		: null;

	// Helper to convert Record<string, string> to ComboboxItem[]
	const recordToItems = (record: Record<string, string> = {}): ComboboxItem[] => {
		return Object.entries(record).map(([id, label]) => ({ id, label }));
	};

	// Helper to get display value from selected IDs
	const getDisplayValue = (selectedIds: string[] = [], availableFields: Record<string, string> = {}): string => {
		if (!selectedIds?.length) return 'None selected';
		return selectedIds.map(id => availableFields[id] || id).join(', ');
	};

	const onUpdate = (
		updatedSettings:
			| Partial<Api.IMicrosoftDynamics365SalesKeyFactSettings>
			| Partial<Api.ISalesforceKeyFactSettings>
			| Partial<Api.IServiceTitanKeyFactSettings>
			| Partial<Api.IKeyFactSettings>
	) => {
		setKeyFactsSettings({
			...keyFactSettings,
			...updatedSettings,
		});
	};

	const handleCustomFieldChange = (selectedCustomFields: string[]) => {
		onUpdate({ enabledCustomDateFields: selectedCustomFields });
	};

	const handleDateFieldChange = (selectedFields: string[]) => {
		if (isSalesforceSettings && salesforceKeyFactSettings) {
			onUpdate({ enabledDateFields: selectedFields });
		}

		if (isServiceTitanSettings && serviceTitanKeyFactSettings) {
			onUpdate({ enabledDateFields: selectedFields });
		}
	};

	const showBirthdayDisplay = React.useMemo(() => {
		return (
			provider !== Api.IntegrationProvider.MicrosoftDynamics365Sales &&
			provider !== Api.IntegrationProvider.Salesforce &&
			provider !== Api.IntegrationProvider.ServiceTitan
		);
	}, [provider]);

	return (
		<SettingGroup title='Key Facts Settings'>
			{showBirthdayDisplay ? (
				<SettingRow title='Birthday Display' subtitle='Determines type of contact to show birthdays for'>
					<EditModeSelect
						items={birthdayDisplayOptions}
						value={keyFactSettings?.birthdayDisplay ?? null}
						onValueChange={(value: string) => onUpdate({ birthdayDisplay: value as Api.BirthdayKeyFactDisplay })}
						displayValue={keyFactSettings?.birthdayDisplay ?? 'Select birthday display'}
					>
						<SelectTrigger styles={styleSheet.editableField}>
							<SelectValue placeholder='Select birthday display' />
						</SelectTrigger>
						<SelectContent>
							{birthdayDisplayOptions.map(option => (
								<SelectItem key={option.value} value={option.value}>
									{option.label}
								</SelectItem>
							))}
						</SelectContent>
					</EditModeSelect>
				</SettingRow>
			) : null}

			{isPracticePantherSettings && ppKeyFactSettings && (
				<>
					{/* Now we can safely access PracticePanther-specific settings */}
					{/* <SettingRow title='Custom Key Facts' subtitle={ppSettings.customKeyFactsEnabled ? 'Enabled' : 'Disabled'}>
						<Toggle
							id='customKeyFacts'
							isOn={ppSettings.customKeyFactsEnabled}
							onToggleCheckChanged={checked => onUpdate({ ...ppSettings, customKeyFactsEnabled: checked })}
						/>
					</SettingRow> */}
				</>
			)}

			{isMicrosoftDynamics365SalesSettings && microsoftDynamics365SalesKeyFactSettings ? (
				<>
					<SettingRow title='Custom Fields' subtitle='Select custom date fields to be used as key facts'>
						<EditModeCombobox
							multiple
							items={recordToItems(microsoftDynamics365SalesConfiguration?.availableCustomDateFields)}
							value={microsoftDynamics365SalesKeyFactSettings.enabledCustomDateFields || []}
							onChange={handleCustomFieldChange}
							displayValue={getDisplayValue(
								microsoftDynamics365SalesKeyFactSettings.enabledCustomDateFields,
								microsoftDynamics365SalesConfiguration?.availableCustomDateFields
							)}
							searchPlaceholder='Search custom fields...'
						/>
					</SettingRow>
				</>
			) : null}

			{isServiceTitanSettings && serviceTitanKeyFactSettings ? (
				<>
					<SettingRow title='Date Fields' subtitle='Select date fields to be used as key facts'>
						<EditModeCombobox
							multiple
							items={recordToItems(serviceTitanConfiguration?.availableDateFields)}
							value={serviceTitanKeyFactSettings.enabledDateFields || []}
							onChange={handleDateFieldChange}
							displayValue={getDisplayValue(
								serviceTitanKeyFactSettings.enabledDateFields,
								serviceTitanConfiguration?.availableDateFields
							)}
							searchPlaceholder='Search date fields...'
						/>
					</SettingRow>
				</>
			) : null}

			{isSalesforceSettings && salesforceKeyFactSettings ? (
				<>
					<SettingRow title='Date Fields' subtitle='Select date fields to be used as key facts'>
						<EditModeCombobox
							multiple
							items={recordToItems(salesforceConfiguration?.availableDateFields)}
							value={salesforceKeyFactSettings.enabledDateFields || []}
							onChange={handleDateFieldChange}
							displayValue={getDisplayValue(
								salesforceKeyFactSettings.enabledDateFields,
								salesforceConfiguration?.availableDateFields
							)}
							searchPlaceholder='Search date fields...'
						/>
					</SettingRow>
					<SettingRow title='Custom Date Fields' subtitle='Select custom date fields to be used as key facts'>
						<EditModeCombobox
							multiple
							items={recordToItems(salesforceConfiguration?.availableCustomDateFields)}
							value={salesforceKeyFactSettings.enabledCustomDateFields || []}
							onChange={handleCustomFieldChange}
							displayValue={getDisplayValue(
								salesforceKeyFactSettings.enabledCustomDateFields,
								salesforceConfiguration?.availableCustomDateFields
							)}
							searchPlaceholder='Search custom date fields...'
						/>
					</SettingRow>
				</>
			) : null}
		</SettingGroup>
	);
};
